import * as THREE from "three/webgpu";
import type { BattleTeam } from "./BattleInteractionRouter";

const MATRIX = new THREE.Matrix4();
const POSITION = new THREE.Vector3();
const QUATERNION = new THREE.Quaternion();
const SCALE = new THREE.Vector3();
const EULER = new THREE.Euler();
const COLOR = new THREE.Color();
const DELTA = new THREE.Vector3();
const MID = new THREE.Vector3();
const PERP = new THREE.Vector3();
const Y_AXIS = new THREE.Vector3(0, 1, 0);
const HIP = new THREE.Vector3();
const FOOT = new THREE.Vector3();
const KNEE = new THREE.Vector3();

export const BIPED_DURATION = 4.6;
export const HOUND_DURATION = 3.4;

function hash(value: number): number {
  let result = value | 0;
  result = Math.imul(result ^ (result >>> 16), 0x21f0aaad);
  result = Math.imul(result ^ (result >>> 15), 0x735a2d97);
  return ((result ^ (result >>> 15)) >>> 0) / 0xffffffff;
}

function setInstance(
  mesh: THREE.InstancedMesh,
  index: number,
  x: number,
  y: number,
  z: number,
  sx: number,
  sy: number,
  sz: number,
  rx = 0,
  ry = 0,
  rz = 0,
): void {
  POSITION.set(x, y, z);
  EULER.set(rx, ry, rz);
  QUATERNION.setFromEuler(EULER);
  SCALE.set(sx, sy, sz);
  MATRIX.compose(POSITION, QUATERNION, SCALE);
  mesh.setMatrixAt(index, MATRIX);
}

function solveKnee(hip: THREE.Vector3, foot: THREE.Vector3, length: number, bend: number, target: THREE.Vector3): THREE.Vector3 {
  DELTA.subVectors(foot, hip);
  const distance = THREE.MathUtils.clamp(DELTA.length(), 0.08, length * 1.98);
  MID.copy(hip).add(foot).multiplyScalar(0.5);
  PERP.set(-DELTA.y, DELTA.x, 0);
  if (PERP.lengthSq() < 0.0001) PERP.set(bend, 0, 0);
  PERP.normalize().multiplyScalar(Math.sqrt(Math.max(0, length * length - distance * distance * 0.25)) * bend);
  return target.copy(MID).add(PERP);
}

function setLimb(mesh: THREE.InstancedMesh, index: number, from: THREE.Vector3, to: THREE.Vector3, width = 1): void {
  DELTA.subVectors(to, from);
  const length = Math.max(0.01, DELTA.length());
  POSITION.copy(from).add(to).multiplyScalar(0.5);
  QUATERNION.setFromUnitVectors(Y_AXIS, DELTA.normalize());
  SCALE.set(width, length, width);
  MATRIX.compose(POSITION, QUATERNION, SCALE);
  mesh.setMatrixAt(index, MATRIX);
}

function disposeGroup(group: THREE.Group): void {
  group.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.geometry.dispose();
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const material of materials) material.dispose();
  });
  group.removeFromParent();
}

/** Two-legged gift mech. Both feet are planted by analytic two-bone IK. */
export class BattleBipedMech {
  readonly root = new THREE.Group();
  readonly #armor: THREE.InstancedMesh<THREE.BoxGeometry, THREE.MeshToonMaterial>;
  readonly #joints: THREE.InstancedMesh<THREE.IcosahedronGeometry, THREE.MeshToonMaterial>;
  readonly #limbs: THREE.InstancedMesh<THREE.CylinderGeometry, THREE.MeshToonMaterial>;
  readonly #glow: THREE.InstancedMesh<THREE.BoxGeometry, THREE.MeshBasicMaterial>;
  readonly #rings: THREE.InstancedMesh<THREE.TorusGeometry, THREE.MeshBasicMaterial>;
  readonly #sparks: THREE.InstancedMesh<THREE.TetrahedronGeometry, THREE.MeshBasicMaterial>;
  #attacker: BattleTeam = "ember";
  #startedAt = -10;

  constructor(scene: THREE.Scene) {
    this.root.name = "gift-biped-ik-mech";
    scene.add(this.root);
    this.#armor = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshToonMaterial({ color: 0xffffff, vertexColors: true }),
      9,
    );
    this.#armor.frustumCulled = false;
    this.#armor.castShadow = true;
    this.root.add(this.#armor);
    this.#joints = new THREE.InstancedMesh(
      new THREE.IcosahedronGeometry(0.14, 1),
      new THREE.MeshToonMaterial({ color: 0x243344 }),
      8,
    );
    this.#joints.frustumCulled = false;
    this.root.add(this.#joints);
    this.#limbs = new THREE.InstancedMesh(
      new THREE.CylinderGeometry(0.09, 0.12, 1, 7),
      new THREE.MeshToonMaterial({ color: 0x435363 }),
      4,
    );
    this.#limbs.frustumCulled = false;
    this.root.add(this.#limbs);
    this.#glow = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({ color: 0xffe27c, transparent: true, opacity: 0.9, depthWrite: false, blending: THREE.AdditiveBlending }),
      5,
    );
    this.#glow.frustumCulled = false;
    this.#glow.renderOrder = 9;
    this.root.add(this.#glow);
    this.#rings = new THREE.InstancedMesh(
      new THREE.TorusGeometry(1, 0.035, 5, 42),
      new THREE.MeshBasicMaterial({ color: 0xffdf75, transparent: true, opacity: 0.65, depthWrite: false, blending: THREE.AdditiveBlending }),
      8,
    );
    this.#rings.frustumCulled = false;
    this.root.add(this.#rings);
    this.#sparks = new THREE.InstancedMesh(
      new THREE.TetrahedronGeometry(0.055, 0),
      new THREE.MeshBasicMaterial({ color: 0xffeea9, transparent: true, opacity: 0.85, depthWrite: false, blending: THREE.AdditiveBlending }),
      64,
    );
    this.#sparks.frustumCulled = false;
    this.root.add(this.#sparks);
    this.root.visible = false;
  }

  trigger(attacker: BattleTeam, time: number): void {
    this.#attacker = attacker;
    this.#startedAt = time;
    const team = attacker === "ember" ? 0xe76658 : 0x44c4c4;
    const palette = [0x314255, team, 0xffd06b, team, team, 0x273847, 0x273847, 0x273847, 0x273847];
    for (let index = 0; index < palette.length; index += 1) this.#armor.setColorAt(index, COLOR.set(palette[index]!));
    if (this.#armor.instanceColor) this.#armor.instanceColor.needsUpdate = true;
    this.#glow.material.color.setHex(attacker === "ember" ? 0xffcb68 : 0x8cffff);
    this.root.position.z = attacker === "ember" ? -1.65 : 1.65;
    this.root.scale.set(1, 1.25, 1.25);
  }

  update(time: number, reducedMotion: boolean): void {
    const age = time - this.#startedAt;
    const active = age >= 0 && age < BIPED_DURATION;
    this.root.visible = active;
    if (!active) return;
    const direction = this.#attacker === "ember" ? 1 : -1;
    const progress = THREE.MathUtils.clamp(age / BIPED_DURATION, 0, 1);
    const travel = THREE.MathUtils.smootherstep(progress, 0.03, 0.92);
    const x = THREE.MathUtils.lerp(-direction * 4.8, direction * 3.85, travel);
    const gait = time * (reducedMotion ? 3.2 : 6.4);
    const bodyBounce = Math.abs(Math.sin(gait)) * 0.08;
    const yaw = direction > 0 ? 0 : Math.PI;

    setInstance(this.#armor, 0, x, 1.62 + bodyBounce, 0, 0.68, 0.48, 0.46, 0, yaw, -direction * 0.05);
    setInstance(this.#armor, 1, x - direction * 0.04, 1.16, 0, 0.43, 0.19, 0.35, 0, yaw, 0);
    setInstance(this.#armor, 2, x + direction * 0.1, 2.16 + bodyBounce, 0, 0.34, 0.27, 0.31, 0, yaw, 0);
    setInstance(this.#armor, 3, x + direction * 0.34, 1.68, -0.5, 0.28, 0.18, 0.22, 0, yaw, 0);
    setInstance(this.#armor, 4, x + direction * 0.34, 1.68, 0.5, 0.28, 0.18, 0.22, 0, yaw, 0);
    const barrelX = x + direction * 0.68;
    setInstance(this.#armor, 5, barrelX, 1.69, -0.5, 0.42, 0.08, 0.09, 0, 0, 0);
    setInstance(this.#armor, 6, barrelX, 1.69, 0.5, 0.42, 0.08, 0.09, 0, 0, 0);

    let jointSlot = 0;
    let limbSlot = 0;
    for (let side = -1; side <= 1; side += 2) {
      const phase = gait + (side > 0 ? Math.PI : 0);
      HIP.set(x, 1.15 + bodyBounce, side * 0.27);
      FOOT.set(
        x + direction * Math.sin(phase) * 0.46,
        0.16 + Math.max(0, Math.cos(phase)) * (reducedMotion ? 0.08 : 0.3),
        side * 0.31,
      );
      solveKnee(HIP, FOOT, 0.62, direction, KNEE);
      setLimb(this.#limbs, limbSlot, HIP, KNEE, 1.1);
      limbSlot += 1;
      setLimb(this.#limbs, limbSlot, KNEE, FOOT, 0.92);
      limbSlot += 1;
      for (const point of [HIP, KNEE, FOOT]) {
        setInstance(this.#joints, jointSlot, point.x, point.y, point.z, 1, 1, 1);
        jointSlot += 1;
      }
      setInstance(this.#armor, side < 0 ? 7 : 8, FOOT.x + direction * 0.08, 0.1, FOOT.z, 0.34, 0.09, 0.25, 0, 0, 0);
    }
    this.#armor.count = 9;
    this.#joints.count = jointSlot;
    this.#limbs.count = limbSlot;
    for (const mesh of [this.#armor, this.#joints, this.#limbs]) mesh.instanceMatrix.needsUpdate = true;

    setInstance(this.#glow, 0, x + direction * 0.36, 2.18, 0, 0.19, 0.06, 0.25);
    setInstance(this.#glow, 1, x - direction * 0.69, 1.65, -0.31, 0.08, 0.16, 0.1);
    setInstance(this.#glow, 2, x - direction * 0.69, 1.65, 0.31, 0.08, 0.16, 0.1);
    setInstance(this.#glow, 3, x + direction * 1.12, 1.69, -0.5, 0.18, 0.035, 0.035);
    setInstance(this.#glow, 4, x + direction * 1.12, 1.69, 0.5, 0.18, 0.035, 0.035);
    this.#glow.instanceMatrix.needsUpdate = true;

    this.#updateStompFx(time, gait, x, direction, reducedMotion);
  }

  dispose(): void {
    disposeGroup(this.root);
  }

  #updateStompFx(time: number, gait: number, x: number, direction: number, reducedMotion: boolean): void {
    const ringCount = reducedMotion ? 4 : 8;
    this.#rings.count = ringCount;
    for (let index = 0; index < ringCount; index += 1) {
      const phase = (time * 1.55 + index / ringCount) % 1;
      const side = index % 2 === 0 ? -1 : 1;
      POSITION.set(x - direction * phase * 1.4, 0.08, side * 0.3);
      EULER.set(Math.PI / 2, 0, 0);
      QUATERNION.setFromEuler(EULER);
      SCALE.setScalar(0.12 + phase * 0.72);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#rings.setMatrixAt(index, MATRIX);
    }
    this.#rings.instanceMatrix.needsUpdate = true;

    const sparkCount = reducedMotion ? 24 : 64;
    this.#sparks.count = sparkCount;
    for (let index = 0; index < sparkCount; index += 1) {
      const seed = hash(index * 47 + 13);
      const t = (time * (1.2 + seed * 0.4) + seed * 4) % 0.55;
      const side = index % 2 === 0 ? -1 : 1;
      POSITION.set(
        x - direction * (0.15 + t * (0.5 + seed)),
        0.08 + Math.max(0, (1.8 + seed * 2.4) * t - 5.2 * t * t),
        side * 0.3 + (seed - 0.5) * 0.9,
      );
      EULER.set(gait + seed * 4, seed * 8, time * 2);
      QUATERNION.setFromEuler(EULER);
      SCALE.setScalar(0.7 + seed * 1.6);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#sparks.setMatrixAt(index, MATRIX);
    }
    this.#sparks.instanceMatrix.needsUpdate = true;
  }
}

/** Fast quadruped robot with four independently solved two-bone IK legs. */
export class BattleQuadrupedHound {
  readonly root = new THREE.Group();
  readonly #armor: THREE.InstancedMesh<THREE.BoxGeometry, THREE.MeshToonMaterial>;
  readonly #joints: THREE.InstancedMesh<THREE.IcosahedronGeometry, THREE.MeshToonMaterial>;
  readonly #limbs: THREE.InstancedMesh<THREE.CylinderGeometry, THREE.MeshToonMaterial>;
  readonly #glow: THREE.InstancedMesh<THREE.ConeGeometry, THREE.MeshBasicMaterial>;
  readonly #trails: THREE.InstancedMesh<THREE.CylinderGeometry, THREE.MeshBasicMaterial>;
  readonly #rings: THREE.InstancedMesh<THREE.TorusGeometry, THREE.MeshBasicMaterial>;
  #attacker: BattleTeam = "ember";
  #startedAt = -10;

  constructor(scene: THREE.Scene) {
    this.root.name = "gift-quadruped-ik-hound";
    scene.add(this.root);
    this.#armor = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshToonMaterial({ color: 0xffffff, vertexColors: true }), 11);
    this.#armor.frustumCulled = false;
    this.#armor.castShadow = true;
    this.root.add(this.#armor);
    this.#joints = new THREE.InstancedMesh(new THREE.IcosahedronGeometry(0.11, 1), new THREE.MeshToonMaterial({ color: 0x263849 }), 12);
    this.#joints.frustumCulled = false;
    this.root.add(this.#joints);
    this.#limbs = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.07, 0.09, 1, 7), new THREE.MeshToonMaterial({ color: 0x445766 }), 8);
    this.#limbs.frustumCulled = false;
    this.root.add(this.#limbs);
    this.#glow = new THREE.InstancedMesh(
      new THREE.ConeGeometry(0.12, 0.42, 6),
      new THREE.MeshBasicMaterial({ color: 0xffda70, transparent: true, opacity: 0.9, depthWrite: false, blending: THREE.AdditiveBlending }),
      7,
    );
    this.#glow.frustumCulled = false;
    this.root.add(this.#glow);
    this.#trails = new THREE.InstancedMesh(
      new THREE.CylinderGeometry(0.018, 0.045, 1, 5),
      new THREE.MeshBasicMaterial({ color: 0x9affff, transparent: true, opacity: 0.76, depthWrite: false, blending: THREE.AdditiveBlending }),
      72,
    );
    this.#trails.frustumCulled = false;
    this.root.add(this.#trails);
    this.#rings = new THREE.InstancedMesh(
      new THREE.TorusGeometry(1, 0.045, 5, 44),
      new THREE.MeshBasicMaterial({ color: 0x9affff, transparent: true, opacity: 0.7, depthWrite: false, blending: THREE.AdditiveBlending }),
      6,
    );
    this.#rings.frustumCulled = false;
    this.root.add(this.#rings);
    this.root.visible = false;
  }

  trigger(attacker: BattleTeam, time: number): void {
    this.#attacker = attacker;
    this.#startedAt = time;
    const team = attacker === "ember" ? 0xdd5e54 : 0x3ebac0;
    const palette = [0x304253, team, 0xffd16c, 0x263746, team, 0x263746, 0x263746, 0x263746, 0x263746, team, team];
    for (let index = 0; index < palette.length; index += 1) this.#armor.setColorAt(index, COLOR.set(palette[index]!));
    if (this.#armor.instanceColor) this.#armor.instanceColor.needsUpdate = true;
    this.#glow.material.color.setHex(attacker === "ember" ? 0xffc45e : 0x7af3f4);
    this.#trails.material.color.setHex(attacker === "ember" ? 0xff936f : 0x74f1ed);
    this.#rings.material.color.setHex(attacker === "ember" ? 0xffd477 : 0x8cffff);
    this.root.position.z = attacker === "ember" ? -1.65 : 1.65;
    this.root.scale.set(1, 1.2, 1.2);
  }

  update(time: number, reducedMotion: boolean): void {
    const age = time - this.#startedAt;
    const active = age >= 0 && age < HOUND_DURATION;
    this.root.visible = active;
    if (!active) return;
    const direction = this.#attacker === "ember" ? 1 : -1;
    const progress = THREE.MathUtils.clamp(age / HOUND_DURATION, 0, 1);
    const travel = THREE.MathUtils.smootherstep(progress, 0.02, 0.9);
    const x = THREE.MathUtils.lerp(-direction * 5.15, direction * 4.65, travel);
    const gait = time * (reducedMotion ? 4 : 9.4);
    const bodyY = 1.02 + Math.abs(Math.sin(gait * 0.5)) * 0.1;

    setInstance(this.#armor, 0, x, bodyY, 0, 0.9, 0.32, 0.48, 0, 0, -direction * 0.08);
    setInstance(this.#armor, 1, x + direction * 0.82, bodyY + 0.12, 0, 0.38, 0.31, 0.35, 0, 0, -direction * 0.12);
    setInstance(this.#armor, 2, x + direction * 1.12, bodyY + 0.1, 0, 0.28, 0.2, 0.28);
    setInstance(this.#armor, 3, x - direction * 0.82, bodyY + 0.08, 0, 0.25, 0.22, 0.34);
    setInstance(this.#armor, 4, x, bodyY + 0.38, 0, 0.54, 0.08, 0.32);
    for (let index = 0; index < 4; index += 1) {
      const front = index < 2 ? 1 : -1;
      const side = index % 2 === 0 ? -1 : 1;
      const phase = gait + (front > 0 ? 0 : Math.PI) + (side > 0 ? Math.PI : 0);
      HIP.set(x + direction * front * 0.58, bodyY - 0.04, side * 0.39);
      FOOT.set(
        x + direction * (front * 0.64 + Math.sin(phase) * 0.38),
        0.12 + Math.max(0, Math.cos(phase)) * (reducedMotion ? 0.06 : 0.24),
        side * 0.48,
      );
      solveKnee(HIP, FOOT, 0.49, direction * front, KNEE);
      setLimb(this.#limbs, index * 2, HIP, KNEE, 1.05);
      setLimb(this.#limbs, index * 2 + 1, KNEE, FOOT, 0.88);
      for (const [slot, point] of [[index * 3, HIP], [index * 3 + 1, KNEE], [index * 3 + 2, FOOT]] as const) {
        setInstance(this.#joints, slot, point.x, point.y, point.z, 1, 1, 1);
      }
      setInstance(this.#armor, 5 + index, FOOT.x + direction * 0.08, 0.075, FOOT.z, 0.24, 0.07, 0.2);
    }
    this.#armor.count = 9;
    this.#joints.count = 12;
    this.#limbs.count = 8;
    for (const mesh of [this.#armor, this.#joints, this.#limbs]) mesh.instanceMatrix.needsUpdate = true;

    for (let index = 0; index < 7; index += 1) {
      const gx = x - direction * (0.62 - index * 0.2);
      POSITION.set(gx, bodyY + 0.5 + Math.sin(index * 1.4) * 0.06, 0);
      EULER.set(0, 0, direction * (Math.PI / 2 + 0.35));
      QUATERNION.setFromEuler(EULER);
      SCALE.setScalar(0.72 + Math.sin(time * 10 + index) * 0.14);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#glow.setMatrixAt(index, MATRIX);
    }
    this.#glow.instanceMatrix.needsUpdate = true;
    this.#updateChargeFx(time, x, direction, reducedMotion);
  }

  dispose(): void {
    disposeGroup(this.root);
  }

  #updateChargeFx(time: number, x: number, direction: number, reducedMotion: boolean): void {
    const trailCount = reducedMotion ? 28 : 72;
    this.#trails.count = trailCount;
    for (let index = 0; index < trailCount; index += 1) {
      const seed = hash(index * 67 + 19);
      const length = 0.45 + seed * 1.8;
      POSITION.set(x - direction * (0.8 + hash(index * 43 + 7) * 2.6), 0.12 + hash(index * 31 + 3) * 1.45, (seed - 0.5) * 2.1);
      EULER.set(0, 0, Math.PI / 2);
      QUATERNION.setFromEuler(EULER);
      SCALE.set(0.7, length, 0.7);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#trails.setMatrixAt(index, MATRIX);
    }
    this.#trails.instanceMatrix.needsUpdate = true;

    const ringCount = reducedMotion ? 3 : 6;
    this.#rings.count = ringCount;
    for (let index = 0; index < ringCount; index += 1) {
      const phase = (time * 1.7 + index / ringCount) % 1;
      POSITION.set(x - direction * phase * 1.5, 0.09, 0);
      EULER.set(Math.PI / 2, 0, 0);
      QUATERNION.setFromEuler(EULER);
      SCALE.setScalar(0.18 + phase * 0.95);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#rings.setMatrixAt(index, MATRIX);
    }
    this.#rings.instanceMatrix.needsUpdate = true;
  }
}
